/**
 * Phone numbers leave the portal as the bare 10-digit North American number
 * (`5148257340`): no dashes, spaces or parentheses, and no `+1` country code.
 * The opt-in reply is matched on exactly that value, so a number saved as
 * `514-825-7340` or `+1 514 825 7340` made the customer's YES fail (FD-7805).
 */
export const PHONE_DIGITS = 10;

/**
 * Keeps the digits only and drops a leading `1` country code when the number
 * has eleven digits. Anything that is not a valid number is returned as its
 * digits, so validation can reject it.
 */
export const normalizePhone = (value: string | null | undefined): string => {
  const digits = (value ?? "").replace(/\D/g, "");

  return digits.length === PHONE_DIGITS + 1 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

export const isValidPhone = (value: string | null | undefined): boolean =>
  new RegExp(`^\\d{${PHONE_DIGITS}}$`).test(value ?? "");

/**
 * How a phone number is shown: `514-825-7340`. A value that is not a valid
 * number (an old free-text entry) is shown as it was stored.
 */
export const formatPhone = (value: string | null | undefined): string => {
  const digits = normalizePhone(value);

  return isValidPhone(digits) ? maskPhone(digits) : (value ?? "");
};

/**
 * The `xxx-xxx-xxxx` mask as the number is being typed: `514`, `514-825`,
 * `514-825-7`. More digits than a number holds are left unmasked so the
 * mistake stays visible.
 */
export const maskPhone = (value: string | null | undefined): string => {
  const digits = normalizePhone(value);

  if (digits.length > PHONE_DIGITS) {
    return digits;
  }

  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)]
    .filter(Boolean)
    .join("-");
};

/*
 * The phone field itself (`PhoneInput`): what it holds after a keystroke or a
 * paste, and where the caret goes once the digits are re-dashed.
 */

/** Longest number a field accepts (E.164 allows 15 digits). */
export const MAX_PHONE_DIGITS = 15;

/** Every digit of a value, nothing else. */
export const phoneDigits = (value: string | null | undefined): string => (value ?? "").replace(/\D+/g, "");

/**
 * What a pasted number becomes: its digits, without the country code a copied
 * "+1 (514) 825-7340" carries — a North American area code never starts with 1.
 */
export const pastedPhoneDigits = (text: string): string => {
  const digits = phoneDigits(text);

  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

/**
 * Digits as they are typed, dashed as they fill up: "514", "514-825",
 * "514-825-7340". A longer number (an extension, a foreign one) stays undashed.
 */
export const formatPhoneInput = (digits: string): string => {
  if (digits.length > 10) {
    return digits;
  }

  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return digits;
};

/** The position in `formatted` just after its `count`-th digit. */
export const caretAfterDigits = (formatted: string, count: number): number => {
  if (count <= 0) {
    return 0;
  }

  let seen = 0;

  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) {
      seen += 1;

      if (seen === count) {
        return index + 1;
      }
    }
  }

  return formatted.length;
};

/** The digits a phone field holds after an edit, and how many sit before the caret. */
export interface PhoneEdit {
  digits: string;
  digitsBeforeCaret: number;
}

const capped = ({ digits, digitsBeforeCaret }: PhoneEdit): PhoneEdit => ({
  digits: digits.slice(0, MAX_PHONE_DIGITS),
  digitsBeforeCaret: Math.min(digitsBeforeCaret, MAX_PHONE_DIGITS),
});

/**
 * A keystroke in a dashed field. Deleting a dash removes no digit, so the dash
 * would simply come back: the digit next to it goes instead, on the side the key
 * deletes towards.
 */
export const editedPhone = ({
  typed,
  caret,
  previous,
  forward,
}: {
  /** The field's text after the keystroke. */
  typed: string;
  /** The caret position after the keystroke. */
  caret: number;
  /** The digits the field held before it. */
  previous: string;
  /** Whether the key deletes forward (Delete rather than Backspace). */
  forward: boolean;
}): PhoneEdit => {
  let digits = phoneDigits(typed);
  let digitsBeforeCaret = phoneDigits(typed.slice(0, caret)).length;

  if (digits === previous && typed.length < formatPhoneInput(previous).length) {
    if (forward && digitsBeforeCaret < digits.length) {
      digits = digits.slice(0, digitsBeforeCaret) + digits.slice(digitsBeforeCaret + 1);
    } else if (!forward && digitsBeforeCaret > 0) {
      digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
      digitsBeforeCaret -= 1;
    }
  }

  return capped({ digits, digitsBeforeCaret });
};

/** A paste into a dashed field: only the pasted digits go in, over the selection. */
export const pastedPhone = ({
  shown,
  selectionStart,
  selectionEnd,
  text,
}: {
  shown: string;
  selectionStart: number;
  selectionEnd: number;
  text: string;
}): PhoneEdit => {
  const head = phoneDigits(shown.slice(0, selectionStart));
  const pasted = pastedPhoneDigits(text);

  return capped({
    digits: head + pasted + phoneDigits(shown.slice(selectionEnd)),
    digitsBeforeCaret: head.length + pasted.length,
  });
};
